import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dashboard, SavedAnalysis } from '../types/analytics';
import AnalysisChartRenderer from './AnalysisChartRenderer';
import { Transaction } from '../types';
import { FileSpreadsheet, FileText } from 'lucide-react';

interface DashboardViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboard: Dashboard | null;
  transactions: Transaction[];
  gradingOrders: any[];
  expenses?: any[];
  savedAnalyses?: SavedAnalysis[]; // Latest saved analyses to merge with dashboard analyses
  onChartTypeChange?: (analysis: SavedAnalysis, newChartType: string) => void;
  getValidChartTypes?: (analysis: SavedAnalysis) => string[];
  getAnalysisTypeIcon?: (chartType: string) => string;
  onExportData?: () => void;
  onExportPDF?: (chartElements: Array<{ analysisId: string; element: HTMLElement }>) => Promise<void>;
  onError?: (message: string) => void;
}

const DashboardViewDialog: React.FC<DashboardViewDialogProps> = ({
  open,
  onOpenChange,
  dashboard,
  transactions,
  gradingOrders,
  expenses = [],
  savedAnalyses = [],
  onChartTypeChange,
  getValidChartTypes,
  getAnalysisTypeIcon,
  onExportData,
  onExportPDF,
  onError
}) => {
  if (!dashboard) return null;
  
  // Merge dashboard analyses with latest saved analyses to get updated chart types
  const mergedAnalyses = dashboard.analyses.map(dashboardAnalysis => {
    const latestAnalysis = savedAnalyses.find(a => a.id === dashboardAnalysis.id);
    // Use latest analysis if found, otherwise fall back to dashboard analysis
    return latestAnalysis || dashboardAnalysis;
  });

  const defaultGetIcon = (chartType: string) => {
    switch (chartType) {
      case 'bar': return '📊';
      case 'line': return '📈';
      case 'pie': return '🥧';
      case 'table': return '📋';
      case 'summary': return '📄';
      case 'stacked_bar': return '📊';
      case 'multiple_line': return '📈';
      default: return '📊';
    }
  };

  const iconFunction = getAnalysisTypeIcon || defaultGetIcon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>{dashboard.name}</DialogTitle>
              <DialogDescription>
                Dashboard containing {mergedAnalyses.length} {mergedAnalyses.length === 1 ? 'analysis' : 'analyses'}
              </DialogDescription>
            </div>
            <div className="flex gap-2 mr-8">
              {onExportData && (
                <button
                  type="button"
                  onClick={onExportData}
                  className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-background hover:bg-muted/50 cursor-pointer"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Download Data
                </button>
              )}
              {onExportPDF && (
                <button
                  type="button"
                  onClick={async () => {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    const chartContainers = document.querySelectorAll('[data-chart-container]');
                    const chartElements: Array<{ analysisId: string; element: HTMLElement }> = [];
                    
                    chartContainers.forEach((container, index) => {
                      const analysisId = mergedAnalyses[index]?.id;
                      if (analysisId && container instanceof HTMLElement) {
                        chartElements.push({ analysisId, element: container });
                      }
                    });
                    
                    if (chartElements.length > 0) {
                      await onExportPDF(chartElements);
                    } else {
                      onError?.('Charts not available. Please try again.');
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-background hover:bg-muted/50 cursor-pointer"
                >
                  <FileText className="h-4 w-4" />
                  Download PDF
                </button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {mergedAnalyses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              This dashboard has no analyses yet.
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {mergedAnalyses.map((analysis) => (
                <div key={analysis.id} className="border rounded-lg p-4 space-y-3" data-chart-container>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-lg">{analysis.name}</h3>
                    {onChartTypeChange && getValidChartTypes ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-transparent hover:bg-muted/50 cursor-pointer"
                          >
                            {iconFunction(analysis.chartType || 'bar')} {(analysis.chartType || 'bar').replace('_', ' ').toUpperCase()}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[200px]">
                          <DropdownMenuLabel>Change Chart Type</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {getValidChartTypes(analysis).map((chartType) => (
                            <DropdownMenuItem
                              key={chartType}
                              onClick={() => {
                                if (chartType !== analysis.chartType) {
                                  onChartTypeChange(analysis, chartType);
                                }
                              }}
                              className={chartType === analysis.chartType ? 'bg-muted' : ''}
                            >
                              <span className="mr-2">{iconFunction(chartType)}</span>
                              {chartType === analysis.chartType && '✓ '}
                              {chartType.replace('_', ' ').toUpperCase()}
                              {chartType === analysis.chartType && ' (current)'}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Badge variant="outline">{analysis.chartType || 'bar'}</Badge>
                    )}
                  </div>
                  <div className="border-t pt-3">
                    <AnalysisChartRenderer
                      analysis={analysis}
                      transactions={transactions}
                      gradingOrders={gradingOrders}
                      height={250}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardViewDialog;
